import React, { useEffect, useMemo, useState } from 'react'

import { ApolloProvider } from '@apollo/client'
import { FlagProvider } from '@unleash/proxy-client-react'
import * as queryString from 'query-string'
import { BrowserRouter, Routes, Route } from 'react-router-dom'

import Splash from '~/components/Loading/Splash'
import config, { isGuestRoute } from '~/config'

import { isEmbedded, isProdAdminEmbedded } from '~/helper/isEmbedded'

import { sendAppSignalError } from '~/logging'

import { createApolloClient } from '~/modules/api'

import Authentication, { clearAuthToken, logout, redirectToLogin, setAuthToken } from '~/modules/auth'
import { captureCoralogixError } from '~/modules/coralogix'

import AdminRoot from '~/pages/AdminRoot'
import SessionExpired from '~/pages/Errors/SessionExpired'
import Unexpected from '~/pages/Errors/Unexpected'
import GuestRoot from '~/pages/GuestRoot'
import PrivateRoot from '~/pages/Root'

import ErrorBoundary from './ErrorBoundary'

// `isResponseOk` rejects with the raw Response rather than an Error, so normalise
// before reporting — AppSignal needs something with a message and a stack.
const asRefreshError = (cause: unknown) => {
  if (cause instanceof Error) return cause
  if (cause instanceof Response) return new Error(`Auth token refresh failed with status ${cause.status}`)

  return new Error(`Auth token refresh failed: ${String(cause)}`)
}

// Both sinks, as ErrorBoundary does: AppSignal for the error, Coralogix for the
// client log stream support searches when a user reports being unable to log in.
const reportAuthError = (error: Error, reason: string) => {
  sendAppSignalError(error)
  captureCoralogixError(error, { reason })
}

const App = () => {
  const [expired, setExpired] = useState(false)
  const [refreshing, setRefreshing] = useState(true)

  const unleashConfig = {
    url: config.UNLEASH_URL,
    clientKey: config.UNLEASH_CLIENT_KEY || 'no-key',
    refreshInterval: 15,
    appName: config.UNLEASH_APP_NAME,
    context: {
      properties: {
        platform: 'web',
      },
    },
  }

  const apolloClient = useMemo(() => {
    if (!config.GOLDEN_GATEWAY_API || !config.HODOR_API) return null
    const onAuthExpire = () => setExpired(true)
    return createApolloClient(config.GOLDEN_GATEWAY_API, config.HODOR_API, onAuthExpire)
  }, [])

  useEffect(() => {
    if (isGuestRoute) {
      setRefreshing(false)
      return
    }

    const queryParams = queryString.parse(location.search)
    const queryAuthToken = queryParams.authToken

    if (isEmbedded && isProdAdminEmbedded && !queryAuthToken) {
      window.parent.postMessage({ type: 'getAccessToken', message: undefined }, '*')

      const messageHandler = (event: MessageEvent) => {
        if (event.data?.message) {
          setAuthToken(event.data.message)
          setRefreshing(false)
          window.removeEventListener('message', messageHandler)
        }
      }

      if (window.addEventListener) {
        window.addEventListener('message', messageHandler, false)
      }
    } else {
      if (queryAuthToken) {
        setAuthToken(queryAuthToken as string)
        setRefreshing(false)
      } else {
        clearAuthToken()

        Authentication(config.HODOR_API!)
          .refresh()
          // Scoped to the refresh call itself. Chained after the success handler
          // instead, this also catches a failure to store the token, so a session
          // Hodor just renewed is misread as expired and we bounce the user back to
          // login — the loop reported in PES-11605.
          .catch((cause) => {
            reportAuthError(asRefreshError(cause), 'auth_token_refresh_failed')
            logout().then(redirectToLogin)

            return null
          })
          .then((token) => {
            if (!token) return

            // The token is held in memory regardless, so a failed write is worth
            // reporting but is not a reason to end a working session.
            if (!setAuthToken(token)) {
              reportAuthError(
                new Error('Auth token refreshed but could not be cached in localStorage'),
                'auth_token_cache_failed'
              )
            }

            setRefreshing(false)
          })
      }
    }
  }, [])
  if (expired) return <SessionExpired />
  if (refreshing) return <Splash />
  if (!apolloClient) return <Unexpected />

  return (
    <ErrorBoundary>
      <FlagProvider config={unleashConfig} startClient={Boolean(config.UNLEASH_CLIENT_KEY)}>
        <ApolloProvider client={apolloClient}>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <Routes>
              <Route path="/admin/*" element={<AdminRoot />} />
              <Route path="/guest/*" element={<GuestRoot />} />
              <Route path="/*" element={<PrivateRoot />} />
            </Routes>
          </BrowserRouter>
        </ApolloProvider>
      </FlagProvider>
    </ErrorBoundary>
  )
}

App.displayName = 'App'

export default App
